import { isPlatformBrowser } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, RouterModule } from '@angular/router'
import { isDefined } from '@pai/commons'
import { delay, firstValueFrom, map, Observable, switchMap, takeUntil } from 'rxjs'

import { AnalyticsEvent } from 'app/core/analytics/analytics-event.enum'
import { CmsService, PagedResult } from 'app/core/cms/cms.service'
import {
  ProjectCardItem,
  ProjectCategory,
  PublicationCardItem,
  PublicationCategory,
  SectionFilterItems,
  SectionFilterItems_Filter,
} from 'app/core/cms/generated/api-types'
import { LoggerService } from 'app/core/logger/logger.service'
import { PaginationComponent } from 'app/shared/pagination/pagination.component'
import { ProjectItemCardComponent } from 'app/shared/project-item-card/project-item-card.component'
import { PublicationItemCardComponent } from 'app/shared/publication-item-card/publication-item-card.component'
import { TrackEventDirective } from 'app/shared/track-event.directive'
import { TranslatePipe } from 'app/shared/translate.pipe'
import { QueryParams } from 'app/static/query-params.enum'
import { baseContainerWidth } from 'app/static/style-vars.const'
import { onDestroy } from 'app/utils/rxjs/on-destroy.observable'
import { explicitComputed } from 'app/utils/signal-utils'

import { CmsBlock } from '../../cms-block'
import { ContainerInfo, divideContainerSize } from '../../container-info'
import { LayoutContainer } from '../layout-container'
import { parseDesign } from '../section-utils'

interface FetchItemsParams {
  type: SectionFilterItems_Filter

  /** the ids of the categories to filter by */
  categoryFilter: string[]
  /** zero-based page index */
  page: number
  itemsPerPage: number
}
type ProjectResults = { type: 'projects' } & PagedResult<ProjectCardItem>
type PublicationResults = { type: 'publications' } & PagedResult<PublicationCardItem>
type FetchItemsResults = ProjectResults | PublicationResults

@Component({
  selector: 'pai-section-filter-items',
  templateUrl: './section-filter-items.component.html',
  styleUrls: ['./section-filter-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.size]': 'design().containerWidth',
    '[attr.background]': 'design().background',
    '[attr.innerShadow]': 'design().innerShadow',
  },
  imports: [
    TranslatePipe,
    PaginationComponent,
    RouterModule,
    TrackEventDirective,
    PublicationItemCardComponent,
    ProjectItemCardComponent,
  ],
  providers: [
    { provide: CmsBlock, useExisting: SectionFilterItemsComponent },
    { provide: LayoutContainer, useExisting: SectionFilterItemsComponent },
  ],
})
export class SectionFilterItemsComponent extends CmsBlock<SectionFilterItems> implements LayoutContainer {
  readonly __typename = 'SectionFilterItems'

  protected readonly design = computed(() => parseDesign(this.data().design))

  readonly containerInfo = explicitComputed(
    this.design,
    ({ containerWidth: size }): ContainerInfo => ({
      // 2 cols
      maxWidthAtMd: divideContainerSize(size === 'small' ? `${baseContainerWidth['small']}px` : '100vw', 2),
      // 3 cols
      maxWidthAtLg: divideContainerSize(size === 'large' ? '100vw' : /* sm or md */ `${baseContainerWidth[size]}px`, 3),
      maxWidthAtXl: divideContainerSize(`${baseContainerWidth[size]}px`, 3),
    }),
  )

  protected readonly containerRef = viewChild.required<string, ElementRef<HTMLElement>>('container', {
    read: ElementRef,
  })

  protected readonly queryParams = toSignal(inject(ActivatedRoute).queryParamMap, { requireSync: true })
  protected readonly type = computed(
    (): SectionFilterItems_Filter => (this.data().filter === 'publications' ? 'publications' : 'projects'),
  )
  protected readonly filterEventName = computed(
    (): AnalyticsEvent =>
      this.type() === 'publications' ? AnalyticsEvent.FILTER_PUBLICATION_ITEMS : AnalyticsEvent.FILTER_PROJECT_ITEMS,
  )
  protected readonly categories = computed<ProjectCategory[] | PublicationCategory[]>(
    // unfortunately it is typed as JSON scalar instead of something meaningful (as in the generated TS types)
    () => this.data().categories ?? ([] as any),
  )

  // todo angular@20 use RxResource
  protected readonly fetchParams = computed((): FetchItemsParams => {
    const { itemsPerPage } = this.data()
    const params = this.queryParams()
    const categories = this.categories()
    return {
      type: this.type(),
      itemsPerPage: parseInt(itemsPerPage?.replace('_', '') || '0', 10),
      page: parseInt(params.get(QueryParams.PAGE) || '6', 10),
      categoryFilter: params
        .getAll(QueryParams.FILTER_ITEMS)
        .map((val) => categories.find((i) => i.slug === val))
        .filter(isDefined)
        .map((i) => i.id),
    } as const
  })
  protected readonly cards = {
    error: signal<null | Error>(null),
    isLoading: signal<boolean>(true),
    value: signal<FetchItemsResults | undefined>(void 0),
  } as const

  private readonly cmsService = inject(CmsService)
  private readonly onDestroy$ = onDestroy()
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID))
  private readonly logger = inject(LoggerService).getInstance('SectionFilterItemsComponent')
  private readonly doc: Document = inject(DOCUMENT)

  constructor() {
    super()

    // todo check: cant we just use effect here?
    toObservable(this.cards.value)
      .pipe(delay(50)) // todo: after adding the child animations, trigger this before the animation starts (instead of timing based)
      .subscribe(this.scrollToContainerStart)

    toObservable(this.fetchParams).pipe(takeUntil(this.onDestroy$), switchMap(this.loadItems)).subscribe()
  }

  protected getQueryParam(cat: ProjectCategory | PublicationCategory) {
    return {
      [QueryParams.FILTER_ITEMS]: [cat.slug],
    }
  }

  private readonly loadItems = async (params: FetchItemsParams) => {
    try {
      this.cards.value.set(await firstValueFrom(this.getItems(params)))
      this.cards.error.set(null)
    } catch (error) {
      this.cards.value.set(void 0)
      this.cards.error.set(error instanceof Error ? error : new Error('Unknown error', { cause: error }))
      this.logger.error(error)
    }
  }

  private getItems(params: FetchItemsParams): Observable<FetchItemsResults> {
    switch (params.type) {
      case 'publications':
        return this.cmsService
          .getPublicationCards(params.categoryFilter, params.page, params.itemsPerPage)
          .pipe(map((v): PublicationResults => ({ type: 'publications', ...v })))
      case 'projects':
        return this.cmsService
          .getProjectCards(params.categoryFilter, params.page, params.itemsPerPage)
          .pipe(map((v): ProjectResults => ({ type: 'projects', ...v })))
      default:
        return params.type // actually `never`
    }
  }

  private readonly scrollToContainerStart = () => {
    // after changing pagination we scroll to the start of containerRef
    if (this.isBrowser && this.containerRef().nativeElement) {
      const rect = this.containerRef().nativeElement.getBoundingClientRect()
      if (rect.top < 0) {
        this.doc.documentElement.scrollTop = this.doc.documentElement.scrollTop + rect.top
      }
    }
  }
}
